// officials.js
//
// A member of Congress's full public record from api2: committees, bills,
// votes, campaign money, leadership PACs, companies in their records, stock
// trades and financial disclosure reports. Endpoints, the source switch for
// bills and votes, and the labels the member page needs.
//
// ─── Rules built in (from the representatives API) ────────────────────────────
//
//   - Nothing is added to anything else. The campaign's money, a leadership
//     PAC's money, a company's PAC, its treasury money, its lobbying and the
//     member's trades are separate records filed under separate laws.
//   - A missed vote is a fact about the record, not a reason.
//   - Voting with the party is a comparison, not a judgement, and is not
//     computed for independents.
//   - A company's tie to a member shows a record exists, not an exchange.
//   - Trades are ranges. reported_value is the sum of ranges, not a figure.
//   - Annual reports are linked, not read; no net worth is claimed.

import { fetchApi2 } from './api2';

// ─── Which source serves bills and votes ──────────────────────────────────────
//
// api1 (api.govglance.org) has served House bills and votes on this page for
// a long time; api2 now has both, for both chambers. api2 becomes the source
// only once scripts/compare-member-records.mjs finds it matches api1 -- run it
// and set what it recommends here. With 'api2', api1 stays as the fallback:
// if api2 fails for a member, the page quietly shows api1's list instead.
//
// Senate votes are the exception and always come from api2: api1 has no
// Senate roll calls, so there is nothing to compare and nothing to fall back
// to. That is what finally gives senators a Votes tab.
//
// Votes: api2 since the September 2026 comparison. For three House members it
// had every one of api1's 676 roll calls since 3 January 2025, with every
// position the same. api1's extra roll calls are the 118th Congress, which
// api2 does not hold.
//
// Bills: api2 since the same comparison, re-run with api1's bills matched by
// GovInfo package id. For five members across both chambers, api2 had every
// one of api1's current-Congress bills as sponsor and as cosponsor, plus the
// newest ones api1 had not listed yet. api1's older bills are the 118th
// Congress and earlier, which api2 does not hold.
export const MEMBER_RECORD_SOURCES = { bills: 'api2', votes: 'api2' };

export const sourceFor = (kind, isSenator) => {
  if (kind === 'votes' && isSenator) return 'api2';
  return MEMBER_RECORD_SOURCES[kind] === 'api2' ? 'api2' : 'api1';
};

// ─── Endpoints ────────────────────────────────────────────────────────────────

const base = (id) => `/officials/${encodeURIComponent(id)}`;

export const fetchMemberRecord = (bioguideId) => fetchApi2(`${base(bioguideId)}/record`);

export const fetchMemberBills = (bioguideId, { role = 'sponsor', q, stage, limit = 50, offset = 0 } = {}) =>
  fetchApi2(`${base(bioguideId)}/bills`, { role, q, stage, limit, offset });

export const fetchMemberVotes = (
  bioguideId,
  { q, kind, position, againstParty, limit = 50, offset = 0 } = {}
) =>
  fetchApi2(`${base(bioguideId)}/votes`, {
    q,
    kind,
    position,
    against_party: againstParty ? 'true' : undefined,
    limit,
    offset,
  });

export const fetchMemberTrades = (bioguideId, { limit = 100, offset = 0 } = {}) =>
  fetchApi2(`${base(bioguideId)}/trades`, { limit, offset });

export const fetchMemberCompanies = (bioguideId) => fetchApi2(`${base(bioguideId)}/companies`);

// ─── Labels ───────────────────────────────────────────────────────────────────

export const billStageLabel = (stage, t) =>
  ({
    introduced: t('common:stageIntroduced', 'Introduced'),
    'passed the House': t('common:stagePassedHouse', 'Passed the House'),
    'passed the Senate': t('common:stagePassedSenate', 'Passed the Senate'),
    'passed both chambers': t('common:stagePassedBoth', 'Passed both chambers'),
    'agreed to': t('common:stageAgreedTo', 'Agreed to'),
    'sent to the President': t('common:stageSentToPresident', 'Sent to the President'),
    vetoed: t('common:stageVetoed', 'Vetoed'),
    'became law': t('common:stageBecameLaw', 'Became law'),
  })[stage] || stage;

export const voteKindLabel = (kind, t) =>
  ({
    passage: t('common:voteKindPassage', 'Passage'),
    amendment: t('common:voteKindAmendment', 'Amendment'),
    nomination: t('common:voteKindNomination', 'Nomination'),
    procedural: t('common:voteKindProcedural', 'Procedural'),
    quorum: t('common:voteKindQuorum', 'Quorum call'),
    election: t('common:voteKindElection', 'Election'),
  })[kind] || kind;

export const positionLabel = (position, t) =>
  ({
    yes: t('common:positionYes', 'Yes'),
    no: t('common:positionNo', 'No'),
    present: t('common:positionPresent', 'Present'),
    not_voting: t('common:positionNotVoting', 'Not voting'),
    other: t('common:positionOther', 'Other'),
  })[position] || position;

export const companyRecordLabel = (record, t) =>
  ({
    pac_gave_to_campaign: t('common:tiePac', 'Its PAC gave to the campaign'),
    treasury_to_super_pac: t('common:tieTreasury', 'Gave its own funds to a super PAC that spent in their race'),
    lobbied_their_bill: t('common:tieLobbied', 'Lobbied on a bill they sponsored'),
    ld203_contribution: t('common:tieLd203', 'Reported a contribution to them in lobbying filings'),
    shares_traded: t('common:tieShares', 'They reported trading its shares'),
  })[record] || record;

// api2's bill_key to the bill's GovInfo package id without version letters:
// 119-hr-10473 -> BILLS-119hr10473. congressional_bills keys each printed
// version as that plus its letters (BILLS-119hr10473ih); the detail page finds
// the furthest version it holds.
export const packageIdFromBillKey = (billKey) => {
  const match = /^(\d+)-([a-z]+)-(\d+)$/i.exec(String(billKey || ''));
  return match ? `BILLS-${match[1]}${match[2].toLowerCase()}${Number(match[3])}` : null;
};

// The in-app detail pages api2's rows open, on the feeds' own detail route.
export const billDetailPath = (billKey) => {
  const packageId = packageIdFromBillKey(billKey);
  return packageId ? `/feed/united_states_of_america/congressional_bills/${packageId}` : null;
};

// The vote page reads all_member_votes, which holds House roll calls only, so
// only House votes link to it: by roll-call number, as its own links do, with
// the date to keep a roll call apart from the same number in the other
// session. Senate votes have no page here and keep Source.
export const voteDetailPath = (vote) =>
  vote?.chamber === 'house' && Number.isFinite(Number(vote.number))
    ? `/feed/united_states_of_america/all_member_votes/${Number(vote.number)}` +
      (vote.vote_date ? `?date=${encodeURIComponent(String(vote.vote_date).slice(0, 10))}` : '')
    : null;

// /influence/companies/cik-759944 -> cik-759944
export const orgIdFromUrl = (url) => {
  const match = /\/influence\/companies\/([^/?#]+)/.exec(String(url || ''));
  return match ? decodeURIComponent(match[1]) : null;
};